import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Container, Card, Button, Row, Col, Alert } from "react-bootstrap";
import { useApp } from "../../../contexts/AppContext";
import Icon from "@mdi/react";
import {
  mdiMonitor,
  mdiArrowLeft,
  mdiTrayArrowUp,
  mdiInformation,
} from "@mdi/js";
import CustomToast from "../../../components/common/CustomToast";
import RequestSoftwareModal from "./RequestSoftwareModal";
import HowToExport from "./HowToExport";
import UploadedFileItem from "./UploadedFileItem";
import LeaveWhileProcessingModal from "./LeaveWhileProcessingModal";
import ContinueWithComputerModal from "./ContinueWithComputerModal";
import {
  uploadXlsFile,
  checkTaskStatus,
  addUploadedFile,
  updateFileStatus,
  clearUploadedFiles,
} from "../slice";
import "./createTemplate.css";
import { REPORT_BUG_HELP_LINK } from "../../../constants/global-constants";

// Configuration constants
const POLLING_INTERVAL_MS = 3000; // 3 seconds
const POLLING_TIMEOUT_MS = 300000; // 5 minutes

const CreateTemplate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const {
    device: { isMobile },
  } = useApp();

  const { uploadedFiles } = useSelector((state) => state.templates);

  const [, setSelectedFile] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showLeaveProcessingModal, setShowLeaveProcessingModal] =
    useState(false);
  const [showContinueWithComputerModal, setShowContinueWithComputerModal] =
    useState(false);

  // Handle click on upload section to trigger file input
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      // Reset file input value to ensure onChange fires for same file
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    } else {
      // File input ref is null
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);

    if (files.length > 0) {
      files.forEach((file, index) => {
        const fileId = `${Date.now()}_${index}`; // Unique ID for each file

        // Validate file type (.xls only)
        if (!file.name.endsWith(".xls") && !file.name.endsWith(".xlsx")) {
          // Add file to uploaded files list with FAILURE status
          dispatch(
            addUploadedFile({
              id: fileId,
              name: file.name,
              size: file.size,
              status: "FAILURE",
              error: "Invalid file format. Please import a .xls file.",
            })
          );
          return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          // Add file to uploaded files list with FAILURE status
          dispatch(
            addUploadedFile({
              id: fileId,
              name: file.name,
              size: file.size,
              status: "FAILURE",
              error: "File size must be less than 10MB",
            })
          );
          return;
        }

        // Process valid file
        handleImportTemplate(file, fileId);
      });

      // Clear the selected file state since we're now handling multiple files
      setSelectedFile(null);
    }
  };

  // Handle template import with upload and polling
  const handleImportTemplate = async (file, fileId) => {
    // Add file to uploaded files list with PENDING status
    dispatch(
      addUploadedFile({
        id: fileId,
        name: file.name,
        size: file.size,
        status: "PENDING",
        error: null,
      })
    );

    try {
      // Upload file
      const uploadResult = await dispatch(uploadXlsFile(file)).unwrap();

      const taskId = uploadResult.task_id;

      // Update status to STARTED
      dispatch(updateFileStatus({ fileId, status: "STARTED" }));

      // Start polling for task status
      let pollingAttempts = 0;
      const maxPollingAttempts = POLLING_TIMEOUT_MS / POLLING_INTERVAL_MS;

      const pollTaskStatus = async () => {
        try {
          const statusResult = await dispatch(checkTaskStatus(taskId)).unwrap();
          const status = statusResult.status;

          if (status === "SUCCESS") {
            // Extract relevant fields from result and store them in the file object
            const templateId = statusResult.result?.report_section_template_id;
            const narratives_from_microservice =
              statusResult.result?.narratives_from_microservice;
            const narratives_duplicates_skipped =
              statusResult.result?.narratives_duplicates_skipped;

            dispatch(
              updateFileStatus({
                fileId,
                status,
                templateId,
                narratives_from_microservice,
                narratives_duplicates_skipped,
              })
            );

            // Show success toast
            setSuccessMessage(`Template "${file.name}" created successfully`);
            setShowSuccessToast(true);
            return;
          } else if (status === "FAILURE") {
            // Show error toast
            setErrorMessage(`Couldn't process template "${file.name}"`);
            setShowErrorToast(true);
            dispatch(
              updateFileStatus({
                fileId,
                status: "FAILURE",
                error: statusResult.error || "Processing failed",
              })
            );
            return;
          } else if (
            status === "PENDING" ||
            status === "STARTED" ||
            status === "RETRY"
          ) {
            // Update file status for in-progress states
            dispatch(updateFileStatus({ fileId, status }));
            // Continue polling if not finished and within timeout
            pollingAttempts++;
            if (pollingAttempts < maxPollingAttempts) {
              setTimeout(pollTaskStatus, POLLING_INTERVAL_MS);
            } else {
              // Timeout reached
              setErrorMessage(`Couldn't process template ${file.name}`);
              setShowErrorToast(true);
              dispatch(
                updateFileStatus({
                  fileId,
                  status: "FAILURE",
                  error: "Processing timeout",
                })
              );
            }
          }
        } catch (error) {
          setErrorMessage(`Couldn't process template ${file.name}`);
          setShowErrorToast(true);
          dispatch(
            updateFileStatus({
              fileId,
              status: "FAILURE",
              error: "Status check failed",
            })
          );
        }
      };

      // Start polling
      setTimeout(pollTaskStatus, POLLING_INTERVAL_MS);
    } catch (error) {
      setErrorMessage(`Couldn't process template ${file.name}`);
      setShowErrorToast(true);
      dispatch(
        updateFileStatus({
          fileId,
          status: "FAILURE",
          error: error || "Upload failed",
        })
      );
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (hasProcessingFiles()) {
      setShowLeaveProcessingModal(true);
    } else {
      navigate("/templates");
    }
  };

  // Handle request software support link click
  const handleRequestSupport = (e) => {
    e.preventDefault();
    setShowRequestModal(true);
  };

  // Handle successful modal submission
  const handleModalSubmitSuccess = () => {
    setShowSuccessToast(true);
  };

  // Handle error modal submission
  const handleModalSubmitError = (error) => {
    setErrorMessage(renderError(error));
    setShowErrorToast(true);
  };

  // Helper function to render error message
  const renderError = (error) => {
    if (!error) return "Failed to submit request. Please try again.";

    if (typeof error === "string") {
      return error;
    }

    if (typeof error === "object") {
      if (error.message) {
        return error.message;
      }
      if (error.detail) {
        return error.detail;
      }
    }

    return "Failed to submit request. Please try again.";
  };

  // Handle view button click for uploaded files
  const handleViewTemplate = (templateId) => {
    navigate(`/templates/${templateId}`);
  };

  // Handle help button click for uploaded files
  const handleHelpClick = () => {
    // This can be expanded to show help modal or redirect to help page
    // Help clicked for failed upload
    window.open(REPORT_BUG_HELP_LINK, "_blank");
  };

  // Check if there are any files currently processing
  const hasProcessingFiles = useCallback(() => {
    return uploadedFiles.some(
      (file) =>
        file.status === "PENDING" ||
        file.status === "STARTED" ||
        file.status === "RETRY"
    );
  }, [uploadedFiles]);

  // Handle beforeunload event to prevent leaving while processing
  const handleBeforeUnload = useCallback(
    (e) => {
      if (hasProcessingFiles()) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
        return "";
      }
    },
    [hasProcessingFiles]
  );

  // Handle leave confirmation
  const handleLeaveConfirm = () => {
    setShowLeaveProcessingModal(false);
    // Allow the user to leave by removing the beforeunload listener temporarily
    window.removeEventListener("beforeunload", handleBeforeUnload);
    // Close the page or navigate away
    window.location.href = "/templates";
  };

  // Handle leave cancellation
  const handleLeaveCancel = () => {
    setShowLeaveProcessingModal(false);
  };

  // Handle share URL for import from computer
  const handleShareUrl = async () => {
    const currentUrl = window.location.href;

    // Check if Web Share API is supported
    if (navigator.share) {
      try {
        await navigator.share({
          title: "AI-Powered Inspection - Morph",
          text: "Open this link on your computer to import your template",
          url: currentUrl,
        });

        // Show the modal after sharing
        setShowContinueWithComputerModal(true);
      } catch (error) {
        // User cancelled sharing or error occurred
        console.log("Sharing cancelled or failed:", error);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(currentUrl);
        setSuccessMessage(
          "Link copied to clipboard! Paste it on your computer."
        );
        setShowSuccessToast(true);
        setShowContinueWithComputerModal(true);
      } catch (error) {
        setErrorMessage("Unable to share. Please manually copy the URL.");
        setShowErrorToast(true);
      }
    }
  };

  // Handle continue with computer modal close
  const handleContinueModalClose = () => {
    setShowContinueWithComputerModal(false);
  };

  // Set up beforeunload event listener
  React.useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [uploadedFiles, handleBeforeUnload]); // Re-run when uploadedFiles changes

  // Clear uploaded files when component unmounts (navigating away from route)
  React.useEffect(() => {
    return () => {
      // Clean up uploaded files when leaving the component
      dispatch(clearUploadedFiles());
    };
  }, [dispatch]);

  return (
    <Container fluid className="create-template-container py-4">
      {/* Header */}
      <div className="create-template-header">
        <div className="header-left">
          <Button
            variant="link"
            onClick={handleBack}
            className="p-0 text-decoration-none back-link"
          >
            <Icon path={mdiArrowLeft} size={0.6} className="me-2" />
            Back to Templates
          </Button>
        </div>
        <div className="header-right"></div>
      </div>

      {/* Title */}
      <h1 className="page-title mb-4">Create New Template</h1>

      <Row className="gx-4">
        <Col lg={7} xl={8}>
          {/* Mobile: Import from computer card */}
          {isMobile && (
            <>
              <Card className="import-card mb-2">
                <Card.Body className="import-card-body">
                  <div
                    className="upload-section"
                    onClick={handleShareUrl}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <Icon
                        path={mdiMonitor}
                        size={0.9}
                        className="upload-icon me-3 flex-shrink-0"
                      />
                      <div>
                        <h4 className="import-title">Import from computer</h4>
                        <p className="import-subtitle subtitle-aligned">
                          Send this page's link to your computer to import your
                          template.
                        </p>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Blue banner for mobile */}
              <Alert
                variant="primary"
                className="narrative-upgrade-banner mb-4"
                style={{ padding: "8px 16px" }}
              >
                <div className="banner-content">
                  <Icon
                    path={mdiInformation}
                    size={0.85}
                    className="me-2 flex-shrink-0"
                  />
                  <span>
                    For the best experience, importing from your computer is
                    recommended.
                  </span>
                </div>
              </Alert>
            </>
          )}

          {/* Import Section */}
          <Card className="import-card mb-3">
            <Card.Body className="import-card-body">
              <div
                className="upload-section"
                onClick={handleUploadClick}
                style={{ cursor: "pointer" }}
              >
                <div className="d-flex align-items-center mb-2">
                  <Icon
                    path={mdiTrayArrowUp}
                    size={0.9}
                    className="upload-icon me-3 flex-shrink-0"
                  />
                  <div>
                    <h4 className="import-title">
                      {isMobile
                        ? "Import from phone"
                        : "Import Spectora Templates"}
                    </h4>
                    <p className="import-subtitle subtitle-aligned">
                      {isMobile
                        ? "Select Spectora template files (.xls) from your phone to import."
                        : ".xls files"}
                    </p>
                  </div>
                </div>
                {/* Hidden file input */}
                <input
                  type="file"
                  accept=".xls,.xlsx"
                  multiple
                  onChange={handleFileUpload}
                  className="d-none"
                  ref={fileInputRef}
                />
              </div>
            </Card.Body>
          </Card>

          {/* Support Message */}
          <p className="support-message">
            More inspection software will be supported soon.{" "}
            <button className="step-link" onClick={handleRequestSupport}>
              Request software support →
            </button>
          </p>

          {/* Uploaded Files Status */}
          {uploadedFiles.length > 0 && (
            <div className="uploaded-files-section mt-4">
              {uploadedFiles.map((file) => (
                <UploadedFileItem
                  key={file.id}
                  file={file}
                  onViewClick={handleViewTemplate}
                  onHelpClick={handleHelpClick}
                />
              ))}
            </div>
          )}
        </Col>

        <Col lg={5} xl={4}>
          {/* How to Export Section */}
          <HowToExport isMobile={isMobile} />
        </Col>
      </Row>

      <RequestSoftwareModal
        show={showRequestModal}
        onHide={() => setShowRequestModal(false)}
        onSubmitSuccess={handleModalSubmitSuccess}
        onSubmitError={handleModalSubmitError}
      />

      <LeaveWhileProcessingModal
        show={showLeaveProcessingModal}
        onHide={handleLeaveCancel}
        onConfirm={handleLeaveConfirm}
      />

      {/* Continue with computer modal */}
      <ContinueWithComputerModal
        show={showContinueWithComputerModal}
        onHide={handleContinueModalClose}
      />

      {/* Success Toast */}
      <CustomToast
        show={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        message={successMessage || "Thank you for your feedback!"}
        variant="success"
        position="bottom-end"
        isMobile={isMobile}
        autohide={true}
      />

      {/* Error Toast */}
      <CustomToast
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        message={errorMessage}
        variant="error"
        position="bottom-end"
        isMobile={isMobile}
        autohide={true}
      />
    </Container>
  );
};

export default CreateTemplate;
